// SEM-6249 — pure box/edge layout for the org DIAGRAM view.
//
// Sibling of org-tree-flatten.ts, and deliberately separate from it: flatten
// answers "which rows are visible, in what order" for the INDENTED LIST; this
// answers "where does each box sit on a plane" for the DIAGRAM. Same tree, two
// geometries — merging them would give one function two coordinate systems.
//
// Kept DOM-free for the same reason flatten is: the load-bearing part of a chart
// is the arithmetic, and arithmetic is only cheap to test when nothing renders.
//
// Algorithm — the classic two-pass tidy layout:
//   1. POST-ORDER: a node's subtree width is max(nodeWidth, sum of its visible
//      children's widths + gaps). Leaves and collapsed nodes cost one box.
//   2. PRE-ORDER: hand each node the horizontal span its subtree earned, centre
//      the node in it, then centre the children block beneath it.
//
// Both passes use an explicit stack. This is not stylistic: org-tree-flatten
// made the same call, because spec 68 allows 50k nodes per tenant and a
// recursive walk over a pathological manager chain blows the call stack. Reverse
// pre-order IS a valid post-order (every child precedes its parent), so one
// traversal yields both orderings.
//
// Solid-line only (ADR-012) — the caller passes the same adjacency indexTree
// builds, so dotted-line rows never enter this file.

import type { OrgChartNode } from "./api";

export interface LayoutOptions {
  readonly nodeWidth: number;
  readonly nodeHeight: number;
  /** Horizontal gap between sibling subtrees. */
  readonly hGap: number;
  /** Vertical gap between depth levels. */
  readonly vGap: number;
}

export interface LayoutBox {
  readonly node: OrgChartNode;
  readonly x: number;
  readonly y: number;
  readonly depth: number;
  readonly hasChildren: boolean;
  readonly expanded: boolean;
  /**
   * Descendants this box hides because it is collapsed — 0 when expanded.
   *
   * Surfaced so a collapsed node can SAY how much is behind it. A diagram that
   * silently omits 400 people looks the same as one charting a 12-person
   * company, and the reader has no way to tell which they are looking at.
   */
  readonly hiddenDescendants: number;
}

export interface LayoutEdge {
  readonly parentId: string;
  readonly childId: string;
  /** Parent's bottom-centre. */
  readonly fromX: number;
  readonly fromY: number;
  /** Child's top-centre. */
  readonly toX: number;
  readonly toY: number;
}

export interface DiagramLayout {
  readonly boxes: readonly LayoutBox[];
  readonly edges: readonly LayoutEdge[];
  /** Bounding size of the drawn diagram, in px. */
  readonly width: number;
  readonly height: number;
}

/**
 * Total descendants per node over the WHOLE tree — including subtrees the
 * current `expanded` set hides, which is the entire point (see
 * LayoutBox.hiddenDescendants).
 */
export function countDescendants(
  roots: readonly OrgChartNode[],
  childrenOf: ReadonlyMap<string | null, OrgChartNode[]>,
): Map<string, number> {
  const counts = new Map<string, number>();
  const preOrder: OrgChartNode[] = [];
  const stack: OrgChartNode[] = [...roots];
  while (stack.length > 0) {
    const node = stack.pop()!;
    preOrder.push(node);
    for (const child of childrenOf.get(node.employeeId) ?? []) stack.push(child);
  }
  // Reverse pre-order: every child is settled before its parent is read.
  for (let i = preOrder.length - 1; i >= 0; i -= 1) {
    const node = preOrder[i]!;
    let total = 0;
    for (const child of childrenOf.get(node.employeeId) ?? []) {
      total += 1 + (counts.get(child.employeeId) ?? 0);
    }
    counts.set(node.employeeId, total);
  }
  return counts;
}

export function layoutDiagram(
  roots: readonly OrgChartNode[],
  childrenOf: ReadonlyMap<string | null, OrgChartNode[]>,
  expanded: ReadonlySet<string>,
  opts: LayoutOptions,
): DiagramLayout {
  const { nodeWidth, nodeHeight, hGap, vGap } = opts;
  if (roots.length === 0) return { boxes: [], edges: [], width: 0, height: 0 };

  const descendants = countDescendants(roots, childrenOf);
  const visibleChildren = (node: OrgChartNode): OrgChartNode[] =>
    expanded.has(node.employeeId) ? (childrenOf.get(node.employeeId) ?? []) : [];

  // ── pass 1: subtree widths (post-order) ──────────────────────────────────
  const preOrder: OrgChartNode[] = [];
  const depthOf = new Map<string, number>();
  const seed: { node: OrgChartNode; depth: number }[] = [];
  for (let i = roots.length - 1; i >= 0; i -= 1) seed.push({ node: roots[i]!, depth: 0 });
  while (seed.length > 0) {
    const { node, depth } = seed.pop()!;
    preOrder.push(node);
    depthOf.set(node.employeeId, depth);
    const kids = visibleChildren(node);
    for (let i = kids.length - 1; i >= 0; i -= 1) seed.push({ node: kids[i]!, depth: depth + 1 });
  }

  const widthOf = new Map<string, number>();
  for (let i = preOrder.length - 1; i >= 0; i -= 1) {
    const node = preOrder[i]!;
    const kids = visibleChildren(node);
    if (kids.length === 0) {
      widthOf.set(node.employeeId, nodeWidth);
      continue;
    }
    let span = hGap * (kids.length - 1);
    for (const kid of kids) span += widthOf.get(kid.employeeId)!;
    // No `Math.max(nodeWidth, span)` floor here, and none is needed: a leaf is
    // exactly nodeWidth wide, so by induction every subtree is at least that,
    // and a node with children is at least as wide as one of them. A floor
    // looked prudent and was unreachable — a mutation survived removing it.
    widthOf.set(node.employeeId, span);
  }

  // ── pass 2: x placement (pre-order) ──────────────────────────────────────
  const leftOf = new Map<string, number>();
  let cursor = 0;
  for (const root of roots) {
    leftOf.set(root.employeeId, cursor);
    cursor += widthOf.get(root.employeeId)! + hGap;
  }
  const totalWidth = cursor - hGap;

  const boxes: LayoutBox[] = [];
  const edges: LayoutEdge[] = [];
  const walk: OrgChartNode[] = [];
  for (let i = roots.length - 1; i >= 0; i -= 1) walk.push(roots[i]!);
  let maxDepth = 0;

  while (walk.length > 0) {
    const node = walk.pop()!;
    const id = node.employeeId;
    const left = leftOf.get(id)!;
    const span = widthOf.get(id)!;
    const depth = depthOf.get(id)!;
    const y = depth * (nodeHeight + vGap);
    maxDepth = Math.max(maxDepth, depth);

    const allChildren = childrenOf.get(id) ?? [];
    const kids = visibleChildren(node);
    const isExpanded = expanded.has(id);

    // Children are placed BEFORE the parent's own x is known, because the
    // parent's x is derived from them: it sits over the midpoint of the first
    // and last child's CENTRES, not over the middle of its allocated span. The
    // two agree only when sibling subtrees happen to be equally wide; with an
    // uneven tree the span rule visibly misses (a parent with one wide and one
    // narrow report drifts toward the wide side), and the connector no longer
    // reads as descending into the middle of the row beneath it.
    //
    // Safe against sibling collision by construction: the first child's centre
    // is at least `left + nodeWidth/2` and the last child's at most
    // `left + span - nodeWidth/2`, so their midpoint — and therefore the parent
    // box — always lands inside the span the subtree already owns.
    const kidCentres: number[] = [];
    if (kids.length > 0) {
      let kidsSpan = hGap * (kids.length - 1);
      for (const kid of kids) kidsSpan += widthOf.get(kid.employeeId)!;
      let kidCursor = left + (span - kidsSpan) / 2;
      for (const kid of kids) {
        const kidSpan = widthOf.get(kid.employeeId)!;
        leftOf.set(kid.employeeId, kidCursor);
        kidCentres.push(kidCursor + kidSpan / 2);
        kidCursor += kidSpan + hGap;
      }
    }

    const x =
      kidCentres.length === 0
        ? left + (span - nodeWidth) / 2
        : (kidCentres[0]! + kidCentres[kidCentres.length - 1]!) / 2 - nodeWidth / 2;

    boxes.push({
      node,
      x,
      y,
      depth,
      hasChildren: allChildren.length > 0,
      expanded: isExpanded,
      hiddenDescendants: isExpanded ? 0 : (descendants.get(id) ?? 0),
    });

    for (let i = 0; i < kids.length; i += 1) {
      edges.push({
        parentId: id,
        childId: kids[i]!.employeeId,
        fromX: x + nodeWidth / 2,
        fromY: y + nodeHeight,
        toX: kidCentres[i]!,
        toY: (depth + 1) * (nodeHeight + vGap),
      });
    }
    for (let i = kids.length - 1; i >= 0; i -= 1) walk.push(kids[i]!);
  }

  return {
    boxes,
    edges,
    width: totalWidth,
    height: (maxDepth + 1) * nodeHeight + maxDepth * vGap,
  };
}

/**
 * The `expanded` set a freshly-opened diagram starts with: every node down to
 * `maxDepth`.
 *
 * A diagram is not a list — a list can render 1000 indented rows and let the
 * reader scroll, but 1000 boxes on a plane is both unreadable and slow. Opening
 * bounded, with the hidden counts visible on the frontier, is the honest
 * default: the reader sees the shape and is TOLD what is folded away.
 */
export function defaultExpanded(
  roots: readonly OrgChartNode[],
  childrenOf: ReadonlyMap<string | null, OrgChartNode[]>,
  maxDepth: number,
): Set<string> {
  const expanded = new Set<string>();
  const stack: { node: OrgChartNode; depth: number }[] = [];
  for (const root of roots) stack.push({ node: root, depth: 0 });
  while (stack.length > 0) {
    const { node, depth } = stack.pop()!;
    if (depth >= maxDepth) continue;
    expanded.add(node.employeeId);
    for (const child of childrenOf.get(node.employeeId) ?? []) {
      stack.push({ node: child, depth: depth + 1 });
    }
  }
  return expanded;
}
